export interface EmailRecipientOption {
    email: string
    label?: string
    source?: string
}

interface EmailAddressObject {
    email?: string | null
    email_type?: 'general' | 'billing' | 'other' | string | null
    deleted?: boolean
}

interface EmailTemplateLike {
    send_to?: string[]
    send_to_specific_email?: string | null
}

interface EntityWithEmailAddresses {
    email_addresses?: EmailAddressObject[]
    email?: string | null
}

interface GetEmailTemplateRecipientsParams {
    template: EmailTemplateLike
    objectData: any
}

const isValidEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

const addRecipient = (
    recipients: EmailRecipientOption[],
    email: string | null | undefined,
    label?: string,
    source?: string
) => {
    if (email) {
        const normalizedEmail = email.trim().toLowerCase()
        const alreadyExists = recipients.some(recipient =>
            recipient.email.toLowerCase() === normalizedEmail
        )

        if (normalizedEmail && isValidEmail(normalizedEmail) && !alreadyExists) {
            recipients.push({
                email: normalizedEmail,
                label,
                source
            })
        }
    }
}

const getClientEntity = (objectData: any): EntityWithEmailAddresses | null => {
    return objectData?.client ?? objectData?.recipient ?? null
}

// The contact of a contact page is the page object itself
const getContactEntity = (objectData: any): EntityWithEmailAddresses | null => {
    return objectData?.email_addresses || objectData?.email ? objectData : null
}

const getLocationPersonEntity = (objectData: any): EntityWithEmailAddresses | null => {
    return (
        objectData?.location_person ??
        objectData?.recipient_person ??
        objectData?.client_person ??
        null
    )
}

const addEntityEmailAddresses = (
    recipients: EmailRecipientOption[],
    entity: EntityWithEmailAddresses | null | undefined,
    label: string,
    source: string
) => {
    if (entity) {
        if (entity.email_addresses) {
            entity.email_addresses.forEach(email_address => {
                if (!email_address.deleted) {
                    addRecipient(recipients, email_address.email, label, source)
                }
            })
        }

        if (entity.email) {
            addRecipient(recipients, entity.email, label, source)
        }
    }
}

const addBillingEmailAddress = (
    recipients: EmailRecipientOption[],
    entity: EntityWithEmailAddresses | null | undefined,
    label: string,
    source: string
) => {
    if (entity) {
        const emailAddresses = entity.email_addresses?.filter(email_address =>
            !email_address.deleted &&
            !!email_address.email
        ) ?? []

        const billingEmailAddress = emailAddresses.find(email_address =>
            email_address.email_type === 'billing'
        )

        if (billingEmailAddress) {
            addRecipient(recipients, billingEmailAddress.email, label, source)
        } else if (emailAddresses.length > 0) {
            addRecipient(recipients, emailAddresses[0].email, label, source)
        } else if (entity.email) {
            addRecipient(recipients, entity.email, label, source)
        }
    }
}

export const getEmailTemplateRecipients = ({
    template,
    objectData
}: GetEmailTemplateRecipientsParams): EmailRecipientOption[] => {
    const recipients: EmailRecipientOption[] = []
    const sendToValues = template.send_to ?? []

    sendToValues.forEach(send_to_value => {
        if (send_to_value === 'specific_email') {
            addRecipient(
                recipients,
                template.send_to_specific_email,
                undefined,
                'specific_email'
            )
        }

        if (send_to_value === 'client') {
            addEntityEmailAddresses(
                recipients,
                getClientEntity(objectData),
                'client',
                send_to_value
            )
        }

        if (send_to_value === 'client_company') {
            addEntityEmailAddresses(
                recipients,
                objectData?.client,
                'client_company',
                send_to_value
            )
        }

        if (send_to_value === 'location_person') {
            addEntityEmailAddresses(
                recipients,
                getLocationPersonEntity(objectData),
                'location_person',
                send_to_value
            )
        }

        if (send_to_value === 'contact') {
            addEntityEmailAddresses(
                recipients,
                getContactEntity(objectData),
                'contact',
                send_to_value
            )
        }

        if (send_to_value === 'client_invoice_address') {
            addBillingEmailAddress(
                recipients,
                getClientEntity(objectData),
                'client_invoice_address',
                send_to_value
            )
        }
    })

    return recipients
}

export const recipientsToEmailString = (recipients: EmailRecipientOption[]) => {
    return recipients
        .map(recipient => recipient.email)
        .filter(email => email)
        .join(', ')
}

export const emailStringToRecipients = (value: string | null | undefined): EmailRecipientOption[] => {
    const recipients: EmailRecipientOption[] = []

    if (value) {
        value.split(',').forEach(email => {
            addRecipient(recipients, email)
        })
    }

    return recipients
}